/**
 * Cookbook-ready and Account Create are one screen: the green card holds,
 * then morphs into the signup header graphic. Figma 3804:96883 / 3891:14309.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { ROUTE_MINI_QUIZ_HELLO } from '@hungryroot/routes';
import { Heading, Heart2, Paragraph, Sparkle, Truck } from '@hungryroot/ui-kit';
import { MiniQuizSignupForm } from '../../components/MiniQuizSignupForm/MiniQuizSignupForm';
import { MINI_QUIZ_STORAGE_NAMESPACE } from '../../defs/localStorage';
import { DISCOVER_RECIPES_BENEFIT } from '../../defs/miniQuizCopy';
import {
  SIGNUP_BODY_FADE_DELAY_MS,
  SIGNUP_ENTER_Y,
  SIGNUP_FADE_IN_MS,
  SIGNUP_HOLD_MS,
  SIGNUP_MORPH_EASE,
  SIGNUP_MORPH_MS,
  SIGNUP_TOTAL_MS,
} from '../../defs/miniQuizSignupTiming';
import {
  type LoadingScreenImage,
  useLoadingScreenImages,
} from '../../hooks/useLoadingScreenDefaults';
import { useMiniQuizSignupSuccess } from '../../hooks/useMiniQuizSignupSuccess';
import { getMiniQuizLoginNextRoute } from '../../navigation/getMiniQuizResumeRoute';
import styles from './MiniQuizCookbookSignup.module.css';

const CARD_HOLD_WIDTH = 277;
const CARD_HOLD_HEIGHT = 374;
const CARD_SIGNUP = 148;
const CARD_HEADING_HOLD_GAP = 28;
const SPARKLE_CROSSFADE_MS = SIGNUP_MORPH_MS / 2;

const FALLBACK_PLATE_SRCS = [
  '/public/img/quiz/food-formats/ready-to-eat.jpg',
  '/public/img/quiz/food-formats/heat-and-eat.jpg',
  '/public/img/quiz/food-formats/quick-recipes.jpg',
] as const;

const BENEFITS = [
  {
    copy: 'Save, organize & plan meals with ease',
    Icon: Heart2,
  },
  {
    copy: DISCOVER_RECIPES_BENEFIT,
    Icon: Sparkle,
  },
  {
    copy: 'Seamlessly delivered to your door',
    Icon: Truck,
  },
] as const;

export function MiniQuizCookbookSignup() {
  const { onSuccess } = useMiniQuizSignupSuccess({
    next: null,
    sequenceKey: 'miniQuizVariant2',
  });
  const loadedImages = useLoadingScreenImages(3, MINI_QUIZ_STORAGE_NAMESPACE);
  const loginNext = getMiniQuizLoginNextRoute(
    ROUTE_MINI_QUIZ_HELLO,
    'miniQuizVariant2'
  );
  const plates = useMemo(
    () => resolvePlateSources(loadedImages),
    [loadedImages]
  );
  const [isSignup, setIsSignup] = useState(false);
  const [sparklesHidden, setSparklesHidden] = useState(false);
  const [sparklesSignup, setSparklesSignup] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const [holdOffset, setHoldOffset] = useState(0);
  const [signupOffset, setSignupOffset] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const morphSeconds = shouldReduceMotion ? 0 : SIGNUP_MORPH_MS / 1000;
  const fadeSeconds = shouldReduceMotion ? 0 : SIGNUP_FADE_IN_MS / 1000;
  const bodyDelaySeconds = shouldReduceMotion
    ? 0
    : SIGNUP_BODY_FADE_DELAY_MS / 1000;

  // Hold/signup centering are measured stage offsets, not render-time values.
  // eslint-disable-next-line react-you-might-not-need-an-effect/you-might-not-need-an-effect -- layout measurement
  useLayoutEffect(() => {
    const layer = layerRef.current;
    const body = bodyRef.current;
    if (layer === null || body === null) {
      return;
    }

    const layerHeight = layer.clientHeight;
    const holdGroupHeight = CARD_HOLD_HEIGHT;
    setHoldOffset(Math.max(0, (layerHeight - holdGroupHeight) / 2));

    const signupHeight =
      CARD_SIGNUP + CARD_HEADING_HOLD_GAP + body.offsetHeight;
    setSignupOffset(Math.max(0, (layerHeight - signupHeight) / 2));
  }, []);

  useEffect(() => {
    const signupTimeoutId = window.setTimeout(() => {
      setIsSignup(true);
      setSparklesHidden(true);
    }, SIGNUP_HOLD_MS);
    const sparkleSwapTimeoutId = window.setTimeout(() => {
      setSparklesSignup(true);
      setSparklesHidden(false);
    }, SIGNUP_HOLD_MS + SPARKLE_CROSSFADE_MS);
    const scrollTimeoutId = window.setTimeout(() => {
      setCanScroll(true);
    }, SIGNUP_TOTAL_MS);

    return () => {
      window.clearTimeout(signupTimeoutId);
      window.clearTimeout(sparkleSwapTimeoutId);
      window.clearTimeout(scrollTimeoutId);
    };
  }, []);

  return (
    <div
      className={clsx(
        styles.stage,
        isSignup && styles['is-signup'],
        canScroll && styles['is-scrollable']
      )}
      data-testid="mini-quiz-cookbook-signup"
    >
      <div ref={layerRef} className={styles.layer}>
        <motion.div
          className={styles.enter}
          initial={{ opacity: 0, y: SIGNUP_ENTER_Y }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: fadeSeconds, ease: SIGNUP_MORPH_EASE }}
        >
          <motion.div
            className={styles.stack}
            animate={{
              y: isSignup ? signupOffset : holdOffset,
            }}
            transition={{
              y: {
                duration: isSignup ? morphSeconds : 0,
                ease: SIGNUP_MORPH_EASE,
              },
            }}
          >
            <div className={styles['card-wrap']}>
              <motion.div
                className={styles.card}
                data-testid="mini-quiz-cookbook-card"
                initial={{
                  width: CARD_HOLD_WIDTH,
                  height: CARD_HOLD_HEIGHT,
                }}
                animate={{
                  width: isSignup ? CARD_SIGNUP : CARD_HOLD_WIDTH,
                  height: isSignup ? CARD_SIGNUP : CARD_HOLD_HEIGHT,
                }}
                transition={{
                  duration: morphSeconds,
                  ease: SIGNUP_MORPH_EASE,
                }}
              >
                <motion.div
                  className={styles['hold-heading']}
                  initial={{ opacity: 1 }}
                  animate={{ opacity: isSignup ? 0 : 1 }}
                  transition={{ duration: fadeSeconds }}
                  aria-hidden={isSignup}
                  data-testid="mini-quiz-cookbook-ready-heading"
                >
                  <Heading variant="H1Alt" className={styles['card-heading']}>
                    Your cookbook
                    <br />
                    is ready!
                  </Heading>
                </motion.div>

                <div
                  className={styles.fan}
                  aria-hidden
                  data-testid="mini-quiz-cookbook-fan"
                >
                  {plates.map((src, index) => (
                    <div key={`${src}-${index}`} className={styles.plate}>
                      <img src={src} alt="" className={styles['plate-image']} />
                    </div>
                  ))}
                </div>
              </motion.div>

              {/*
              Solid 4-point sparkles sit outside the clipped card so they
              can overlap its edges. Hold: top-right + pair, middle-left,
              bottom-right (Figma 3804:96883). Signup: top-right,
              bottom-left + pair (Figma 3891:14309).
            */}
              <div
                className={clsx(
                  styles.sparkles,
                  sparklesHidden && styles['is-hidden'],
                  sparklesSignup && styles['is-signup']
                )}
                aria-hidden
                data-faded={sparklesHidden ? 'true' : 'false'}
                data-testid="mini-quiz-cookbook-sparkles"
              >
                <CookbookSparkle
                  className={clsx(styles.sparkle, styles['sparkle-top'])}
                />
                <CookbookSparkle
                  className={clsx(styles.sparkle, styles['sparkle-top-pair'])}
                />
                <CookbookSparkle
                  className={clsx(styles.sparkle, styles['sparkle-mid'])}
                />
                <CookbookSparkle
                  className={clsx(styles.sparkle, styles['sparkle-bottom'])}
                />
                <CookbookSparkle
                  className={clsx(
                    styles.sparkle,
                    styles['sparkle-bottom-pair']
                  )}
                />
              </div>
            </div>

            <motion.div
              ref={bodyRef}
              className={styles.body}
              initial={{ opacity: 0, y: SIGNUP_ENTER_Y }}
              animate={
                isSignup
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: SIGNUP_ENTER_Y }
              }
              transition={
                isSignup
                  ? {
                      duration: morphSeconds,
                      delay: bodyDelaySeconds,
                      ease: SIGNUP_MORPH_EASE,
                    }
                  : { duration: 0 }
              }
              aria-hidden={!isSignup}
            >
              <div
                {...(!isSignup ? { inert: '' } : undefined)}
                data-testid="mini-quiz-cookbook-signup-body"
              >
                <div className={styles.header}>
                  <Heading variant="H1Alt" className={styles.heading}>
                    Your free personal cookbook starts here
                  </Heading>
                  <ul className={styles.benefits}>
                    {BENEFITS.map(({ copy, Icon }) => (
                      <li key={copy} className={styles.benefit}>
                        <Icon className={styles['benefit-icon']} />
                        <Paragraph variant="body">{copy}</Paragraph>
                      </li>
                    ))}
                  </ul>
                </div>

                <MiniQuizSignupForm
                  loginNext={loginNext}
                  onSignupSuccess={onSuccess}
                />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

function CookbookSparkle({ className }: { className?: string }) {
  return (
    <span
      className={className}
      aria-hidden
      data-testid="mini-quiz-cookbook-sparkle"
    >
      <svg
        className={styles['sparkle-image']}
        viewBox="13 5 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M34.6457 6.95927C34.7485 6.40874 35.0406 5.91151 35.4715 5.55369C35.9023 5.19587 36.4447 5 37.0048 5C37.5648 5 38.1073 5.19587 38.5381 5.55369C38.9689 5.91151 39.2611 6.40874 39.3639 6.95927L41.8862 20.2979C42.0653 21.2463 42.5262 22.1185 43.2086 22.801C43.891 23.4834 44.7633 23.9442 45.7116 24.1234L59.0503 26.6457C59.6008 26.7485 60.0981 27.0406 60.4559 27.4715C60.8137 27.9023 61.0096 28.4447 61.0096 29.0048C61.0096 29.5648 60.8137 30.1073 60.4559 30.5381C60.0981 30.9689 59.6008 31.2611 59.0503 31.3639L45.7116 33.8862C44.7633 34.0653 43.891 34.5262 43.2086 35.2086C42.5262 35.891 42.0653 36.7633 41.8862 37.7116L39.3639 51.0503C39.2611 51.6008 38.9689 52.0981 38.5381 52.4559C38.1073 52.8137 37.5648 53.0096 37.0048 53.0096C36.4447 53.0096 35.9023 52.8137 35.4715 52.4559C35.0406 52.0981 34.7485 51.6008 34.6457 51.0503L32.1234 37.7116C31.9442 36.7633 31.4834 35.891 30.801 35.2086C30.1185 34.5262 29.2463 34.0653 28.2979 33.8862L14.9593 31.3639C14.4087 31.2611 13.9115 30.9689 13.5537 30.5381C13.1959 30.1073 13 29.5648 13 29.0048C13 28.4447 13.1959 27.9023 13.5537 27.4715C13.9115 27.0406 14.4087 26.7485 14.9593 26.6457L28.2979 24.1234C29.2463 23.9442 30.1185 23.4834 30.801 22.801C31.4834 22.1185 31.9442 21.2463 32.1234 20.2979L34.6457 6.95927Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
}

function resolvePlateSources(images: LoadingScreenImage[]) {
  const srcs = images.map((image) => image.imageUrl);

  FALLBACK_PLATE_SRCS.forEach((fallback) => {
    if (srcs.length >= 3) {
      return;
    }
    if (!srcs.includes(fallback)) {
      srcs.push(fallback);
    }
  });

  while (srcs.length < 3 && srcs[0] !== undefined) {
    srcs.push(srcs[0]);
  }

  return srcs.slice(0, 3);
}
